import React, {useMemo} from 'react';
import Image from 'next/image';

import {CategoryKeyEnum} from '#apollo/schema';
import {Accordion} from '#component/base';
import {ActionPlanPage} from '#component/page/ActionPlan/ActionPlan.types';
import {GoalsBody} from '#component/partial/ActionPlanGoals/components/GoalsBody';
import {GoalsHeader} from '#component/partial/ActionPlanGoals/components/GoalsHeader';
import {GoalsList} from '#component/partial/ActionPlanGoals/components/GoalsList';
import {AccordionRoot} from '#component/util';
import {sprinkles} from '#style/sprinkles';

import * as css from './ActionPlanGoals.css';

interface ActionPlanGoalsProps {
  actionPlan?: ActionPlanPage.ActionPlan;
  surveySubmissionId?: string;
}
function ActionPlanGoals({actionPlan, surveySubmissionId}: ActionPlanGoalsProps) {
  const goals: ActionPlanPage.ActionPlanAdjustment[] = useMemo(() => {
    if (actionPlan && actionPlan.adjustments) {
      return actionPlan.adjustments.filter(adjustment => {
        return actionPlan.active ? adjustment.selected : adjustment.recommended;
      });
    }
    return [];
  }, [actionPlan]);

  return (
    <>
      <div className={css.showActionPlanGoalsAccordion}>
        <AccordionRoot asChild collapsible type="single">
          <ul className={css.accordionList}>
            {!goals?.length && <EmptyState />}
            {goals.map((goal, i) => (
              <li key={i} className={css.accordionItem}>
                <Accordion
                  triggerClassName={css.trigger}
                  trigger={
                    <GoalsHeader
                      title={goal.healthFactorAssessment?.healthFactor?.title || ''}
                      subtitle={goal.recommendedResponse}
                      healthFactorId={goal.healthFactorAssessment?.healthFactor?.id || ''}
                      categoryKey={
                        goal.healthFactorAssessment?.categoryAssessment?.category?.key ||
                        CategoryKeyEnum.Research
                      }
                      impactLevel={goal.impactLevel || undefined}
                      imageUrl={goal.healthFactorAssessment?.healthFactor?.imageUrl || ''}
                    />
                  }
                  value={`${i}`}>
                  <div className={css.bodyWrapper}>
                    <GoalsBody
                      surveySubmissionId={surveySubmissionId}
                      healthFactorAssessment={goal.healthFactorAssessment}
                      benefits={goal.healthFactorAssessment?.healthFactor?.benefits ?? ''}
                      tips={goal.healthFactorAssessment?.healthFactor?.tips ?? ''}
                      impactLevel={goal.impactLevel}
                    />
                  </div>
                </Accordion>
              </li>
            ))}
          </ul>
        </AccordionRoot>
      </div>
      <div className={css.showActionPlanGoalsListDrawer}>
        <GoalsList goals={goals} surveySubmissionId={surveySubmissionId} />
      </div>
    </>
  );
}

const EmptyState = () => (
  <ul
    className={css.accordionList}
    style={{
      padding: 0,
    }}>
    {Array(3)
      .fill(() => (
        <li className={css.emptyStateItem}>
          <Image
            className={css.lockImage}
            src="/image/checkin-lock.svg"
            alt="no-score"
            height={300}
            width={300}
          />
          <div
            className={sprinkles({
              w: 'full',
              display: 'flex',
              content: 'center',
              dir: 'col',
              h: 'full',
            })}>
            <div className={css.textPlaceholder({size: 'small'})} />
            <div
              className={css.textPlaceholder({
                size: 'large',
              })}
            />
          </div>
        </li>
      ))
      .map((Component, i) => (
        <Component key={i} />
      ))}
  </ul>
);

export {ActionPlanGoals};
